import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import styled from "styled-components";
import {
    MediaPlaceholder,
    ModalCard,
    ModalCancelButton,
    ModalFooter,
    ModalHeaderBlock,
    ModalHeaderTop,
    ModalHeroPill,
    ModalHeroPills,
    ModalPrimaryButton,
    ModalScroll,
    ModalSection,
    ModalSectionHeader,
    ModalSectionNote,
    ModalSectionTitle,
    ModalTitle,
    ModalTitleBlock,
    StackList,
    StackListItem,
    StackListNumber,
    StackListText,
} from "src/scenes/Calendar/components";
import { AUDIENCE_COLORS, getCalendarItemSourceLabel, SHARED_AUDIENCE } from "src/scenes/Calendar/calendarData";
import type { CalendarItem, ThemeMode } from "src/scenes/Calendar/types";
import {
    getAutomationFeature,
    getCalendarItemDisplayColor,
    getDisplayAudienceLabel,
} from "src/scenes/Calendar/utils/calendarHelpers";
import { CheckInDisplay } from "src/scenes/Noticeboard/components/CheckInSection";

type AutomationDetail = NonNullable<CalendarItem["automationDetail"]>;
type RelatedPost = NonNullable<AutomationDetail["previousPosts"]>[number];
type RelatedSurvey = NonNullable<AutomationDetail["otherSurveys"]>[number];
type RelatedPoll = NonNullable<AutomationDetail["otherPolls"]>[number];
type NoticeReaction = Record<string, unknown>;
type NoticeComment = Record<string, unknown> | string;

const NoticeMedia = styled.img`
    width: 100%;
    height: 100%;
    object-fit: contain;
    background: rgba(15, 23, 42, 0.06);
`;

const NoticeVideo = styled.video`
    width: 100%;
    height: 100%;
    background: #000;
`;

const NoticeCarouselFrame = styled.div`
    width: 100%;
    height: 100%;
    position: relative;
    overflow: hidden;
`;

const NoticeCarouselImage = styled.img`
    width: 100%;
    height: 100%;
    min-height: 0;
    display: block;
    object-fit: contain;
    background: rgba(15, 23, 42, 0.06);
`;

const NoticeCarouselVideo = styled.video`
    width: 100%;
    height: 100%;
    min-height: 0;
    display: block;
    background: #000;
`;

const NoticeCarouselButton = styled.button<{ side: "left" | "right" }>`
    width: 34px;
    height: 34px;
    display: grid;
    place-items: center;
    position: absolute;
    top: 50%;
    ${p => p.side}: 12px;
    transform: translateY(-50%);
    border: 0;
    border-radius: 999px;
    background: rgba(15, 23, 42, 0.68);
    color: #FFFFFF;
    font-size: 1.6rem;
    font-weight: 700;
    line-height: 1;
    cursor: pointer;
    z-index: 2;

    &:hover,
    &:focus-visible {
        background: rgba(15, 23, 42, 0.86);
        outline: none;
    }
`;

const NoticeCarouselCount = styled.div`
    position: absolute;
    right: 12px;
    bottom: 12px;
    padding: 6px 10px;
    border-radius: 999px;
    background: rgba(15, 23, 42, 0.72);
    color: #FFFFFF;
    font-size: 0.76rem;
    font-weight: 900;
    line-height: 1.2;
    z-index: 2;
`;

const ContentStack = styled.div`
    display: grid;
    gap: 14px;
`;

const PlainTitle = styled.h3`
    color: var(--calendar-text);
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 1.25rem;
    font-weight: 800;
    line-height: 1.25;
    margin: 0;
    overflow-wrap: anywhere;
`;

const PlainText = styled.p`
    color: var(--calendar-text-muted);
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.95rem;
    font-weight: 500;
    line-height: 1.55;
    margin: 0;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
`;

const PostedDate = styled.div`
    color: var(--calendar-text-muted);
    font-size: 0.8rem;
    font-weight: 800;
    line-height: 1.2;
    text-transform: uppercase;
`;

const EngagementGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 10px;

    @media (max-width: 680px) {
        grid-template-columns: 1fr;
    }
`;

const EngagementCard = styled.div`
    min-width: 0;
    display: grid;
    gap: 6px;
    padding: 12px;
    border: 1px solid #D8DEEC;
    border-radius: var(--calendar-radius-card);
    background: #FFFFFF;
`;

const EngagementLabel = styled.div`
    color: #171A22;
    font-size: 0.8rem;
    font-weight: 900;
    line-height: 1.2;
`;

const EngagementValue = styled.div`
    color: #6A7387;
    font-size: 0.85rem;
    font-weight: 600;
    line-height: 1.4;
`;

const RelatedScroller = styled.div`
    display: grid;
    grid-auto-flow: column;
    grid-auto-columns: minmax(180px, 220px);
    gap: 10px;
    overflow-x: auto;
    padding-bottom: 4px;
`;

const RelatedCard = styled.div`
    min-width: 0;
    display: grid;
    grid-template-rows: auto minmax(0, 1fr);
    gap: 8px;
    padding: 10px;
    border: 1px solid #D8DEEC;
    border-radius: var(--calendar-radius-card);
    background: #FFFFFF;
`;

const RelatedMedia = styled.div`
    width: 100%;
    aspect-ratio: 16 / 9;
    display: grid;
    place-items: center;
    border-radius: var(--calendar-radius-card);
    background: rgba(15, 23, 42, 0.06);
    overflow: hidden;

    img,
    video {
        width: 100%;
        height: 100%;
        display: block;
        object-fit: cover;
    }
`;

const RelatedTitle = styled.div`
    color: #171A22;
    font-size: 0.86rem;
    font-weight: 900;
    line-height: 1.25;
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
`;

const RelatedText = styled.div`
    color: #6A7387;
    font-size: 0.78rem;
    font-weight: 600;
    line-height: 1.35;
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
`;

const PollMediaWrap = styled.div`
    position: relative;
    margin-bottom: 14px;
`;

const CountdownBadge = styled.div`
    position: absolute;
    left: 50%;
    bottom: 12px;
    transform: translateX(-50%);
    max-width: calc(100% - 24px);
    padding: 8px 12px;
    border-radius: 999px;
    background: rgba(15, 23, 42, 0.76);
    color: #fff;
    font-size: 0.8rem;
    font-weight: 900;
    line-height: 1.2;
    text-align: center;
    white-space: nowrap;
`;

const AnswerOption = styled.div`
    min-height: 42px;
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;
    border: 1px solid var(--calendar-border);
    border-radius: var(--calendar-radius-card);
    background: var(--calendar-surface-secondary);
    color: var(--calendar-text);
    font-size: 0.9rem;
    font-weight: 800;
    line-height: 1.3;
`;

const VoteTally = styled.div`
    justify-self: start;
    padding: 7px 10px;
    border-radius: 999px;
    background: rgba(91, 110, 245, 0.12);
    color: var(--calendar-accent);
    font-size: 0.78rem;
    font-weight: 900;
    line-height: 1.2;
`;

const NoticePostCopy = styled.div`
    display: grid;
    gap: 8px;
`;

const formatAutomationDate = (value?: string | null): string => {
    if (!value) return "Not set";
    const date = dayjs(value);
    return date.isValid() ? date.format("MMMM D, YYYY") : value;
};

const hasPollEnded = (value?: string | null, now?: dayjs.Dayjs): boolean => {
    if (!value) {
        return false;
    }

    const end = dayjs(value).endOf("day");
    return end.isValid() && end.valueOf() <= (now || dayjs()).valueOf();
};

const getCountdownLabel = (value?: string | null, now?: dayjs.Dayjs): string => {
    if (!value) {
        return "No end date";
    }

    const end = dayjs(value).endOf("day");
    if (!end.isValid()) {
        return "No end date";
    }

    const currentTime = now || dayjs();
    if (hasPollEnded(value, currentTime)) {
        return "Ended";
    }
    const totalSeconds = end.diff(currentTime, "second");
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${days}d ${hours}h ${minutes}m ${seconds}s`;
};

const compactCountLabel = (count: unknown, singular: string, plural: string): string => {
    if (count === undefined || count === null || count === "") return `0 ${plural}`;
    const numeric = Number(count);
    if (Number.isNaN(numeric)) return String(count);
    return `${numeric} ${numeric === 1 ? singular : plural}`;
};

const formatReaction = (reaction: NoticeReaction): string => {
    const label = reaction.emoji || reaction.type || "Reaction";
    return `${String(label)} ${reaction.count ?? ""}`.trim();
};

const formatComment = (comment: NoticeComment): string => {
    if (typeof comment === "string") return comment;
    return String(comment.message || comment.text || "");
};

const getRelatedPostImage = (post: RelatedPost): string => {
    if (post?.imageUrl) return post.imageUrl;
    if (post?.image?.imageUrl) return post.image.imageUrl;
    const firstCarouselImage = (post?.carousel || []).find(item => item.imageUrl);
    return firstCarouselImage?.imageUrl || "";
};

const countAttachments = (attachments: AutomationDetail["attachments"] | CalendarItem["attachments"]): number => {
    if (!attachments) return 0;
    if (Array.isArray(attachments)) return attachments.length;
    return String(attachments).split(",").filter(attachment => attachment.trim()).length;
};

type Props = {
    themeMode: ThemeMode;
    item: CalendarItem;
    onClose: () => void;
    onViewFeature: (item: CalendarItem) => void;
};

export const VIEWABLE_AUTOMATION_FEATURES = new Set(["noticeboard", "survey", "poll"]);

export function CalendarAutomationDetailModalContent({ themeMode, item, onClose, onViewFeature }: Props) {
    const automationFeature = getAutomationFeature(item);
    const automationDetail = item.automationDetail || {};
    const [now, setNow] = useState(dayjs());
    const [activeCarouselIndex, setActiveCarouselIndex] = useState(0);
    const displayTypeLabel = getCalendarItemSourceLabel(item.source, "Calendar Item");
    const displayTypeColor = automationDetail.featureColor || getCalendarItemDisplayColor(item) || "var(--calendar-accent)";
    const visibilityLabel = getDisplayAudienceLabel(item.audience || SHARED_AUDIENCE.EMPLOYEES);
    const modalTitle = automationFeature === "noticeboard"
        ? "Noticeboard"
        : automationFeature === "survey"
            ? "Surveys"
            : automationFeature === "poll"
                ? "Polls"
                : item.title;

    useEffect(() => {
        if (automationFeature !== "poll") return undefined;
        const timer = window.setInterval(() => setNow(dayjs()), 1000);
        return () => window.clearInterval(timer);
    }, [automationFeature]);

    useEffect(() => {
        setActiveCarouselIndex(0);
    }, [item.id]);

    const renderNoticeboardMedia = (): React.ReactNode => {
        const postType = (automationDetail.postType || "").toLowerCase();
        const carouselItems = [...(automationDetail.carousel || [])]
            .sort((left, right) => (left.carouselOrder ?? 0) - (right.carouselOrder ?? 0))
            .filter(mediaItem => mediaItem.imageUrl || mediaItem.videoUrl);
        const imageUrl = automationDetail.image?.imageUrl || automationDetail.media?.find(mediaItem => mediaItem.type === "image")?.url;
        const videoUrl = automationDetail.video?.videoUrl || automationDetail.media?.find(mediaItem => mediaItem.type === "video")?.url;
        const videoPoster = automationDetail.video?.previewImageUrl || undefined;
        const activeCarouselItem = carouselItems[activeCarouselIndex] || carouselItems[0];
        const goToCarouselItem = (offset: number) => {
            setActiveCarouselIndex(currentIndex => (
                (currentIndex + offset + carouselItems.length) % carouselItems.length
            ));
        };

        return (
            <MediaPlaceholder themeMode={themeMode} ratio="16 / 9">
                {postType === "carousel" && carouselItems.length > 0 ? (
                    <NoticeCarouselFrame>
                        {activeCarouselItem.videoUrl ? (
                            <NoticeCarouselVideo key={activeCarouselItem.uuid || activeCarouselItem.videoUrl} controls preload="metadata" poster={activeCarouselItem.previewImageUrl || undefined}>
                                <source src={activeCarouselItem.videoUrl} type="video/mp4" />
                            </NoticeCarouselVideo>
                        ) : (
                            <NoticeCarouselImage
                                key={activeCarouselItem.uuid || activeCarouselItem.imageUrl}
                                src={activeCarouselItem.imageUrl}
                                alt={`Noticeboard carousel media ${activeCarouselIndex + 1} of ${carouselItems.length}`}
                            />
                        )}
                        {carouselItems.length > 1 && (
                            <>
                                <NoticeCarouselButton side="left" type="button" aria-label="Previous media" onClick={() => goToCarouselItem(-1)}>
                                    ‹
                                </NoticeCarouselButton>
                                <NoticeCarouselButton side="right" type="button" aria-label="Next media" onClick={() => goToCarouselItem(1)}>
                                    ›
                                </NoticeCarouselButton>
                                <NoticeCarouselCount aria-live="polite">
                                    {activeCarouselIndex + 1} / {carouselItems.length}
                                </NoticeCarouselCount>
                            </>
                        )}
                    </NoticeCarouselFrame>
                ) : postType === "video" && videoUrl ? (
                    <NoticeVideo controls preload="metadata" poster={videoPoster || undefined}>
                        <source src={videoUrl} type="video/mp4" />
                    </NoticeVideo>
                ) : imageUrl ? (
                    <NoticeMedia src={imageUrl} alt={automationDetail.title || item.title} />
                ) : (
                    <></>
                )}
            </MediaPlaceholder>
        );
    };

    const renderAutomationContent = () => {
        if (automationFeature === "noticeboard") {
            const postType = (automationDetail.postType || "").toLowerCase();
            const hasMedia = postType === "image" || postType === "video" || postType === "carousel";
            const postedDate = formatAutomationDate(automationDetail.postedDate || automationDetail.scheduleDate || ("dueDate" in item ? item.dueDate : undefined));
            const reactions: NoticeReaction[] = automationDetail.reactions || [];
            const reactionSummary = Array.isArray(reactions) && reactions.length > 0
                ? reactions.map(formatReaction).join("  ")
                : compactCountLabel(automationDetail.reactionCount, "reaction", "reactions");
            const comments: NoticeComment[] = automationDetail.comments || [];
            const commentSummary = Array.isArray(comments) && comments.length > 0
                ? comments.slice(0, 3).map(formatComment).join("\n")
                : automationDetail.commentsEnabled
                    ? compactCountLabel(automationDetail.commentCount, "comment", "comments")
                    : "Comments are off for this post.";
            const attachmentCount = countAttachments(automationDetail.attachments || item.attachments);
            const previousPosts: RelatedPost[] = automationDetail.previousPosts || [];
            const postTitle = automationDetail.postTitle || automationDetail.title || item.title || "Noticeboard post";
            const postMessage = automationDetail.postMessage || automationDetail.message || item.description || "No message available.";

            return (
                <>
                    <ModalSection themeMode={themeMode}>
                        <ContentStack>
                            {hasMedia && renderNoticeboardMedia()}
                            <CheckInDisplay checkIn={automationDetail.checkIn} detail dashboard />
                            <PostedDate>{postedDate}</PostedDate>
                            <NoticePostCopy>
                                <PlainTitle>{postTitle}</PlainTitle>
                                <PlainText>{postMessage}</PlainText>
                            </NoticePostCopy>
                            <EngagementGrid>
                                <EngagementCard>
                                    <EngagementLabel>Reactions</EngagementLabel>
                                    <EngagementValue>{reactionSummary}</EngagementValue>
                                </EngagementCard>
                                <EngagementCard>
                                    <EngagementLabel>Comments</EngagementLabel>
                                    <EngagementValue>{commentSummary}</EngagementValue>
                                </EngagementCard>
                                <EngagementCard>
                                    <EngagementLabel>Attachments</EngagementLabel>
                                    <EngagementValue>{compactCountLabel(attachmentCount, "attachment", "attachments")}</EngagementValue>
                                </EngagementCard>
                            </EngagementGrid>
                        </ContentStack>
                    </ModalSection>
                    {previousPosts.length > 0 && (
                        <ModalSection themeMode={themeMode}>
                            <ModalSectionHeader>
                                <ModalSectionTitle>Previous posts</ModalSectionTitle>
                            </ModalSectionHeader>
                            <RelatedScroller aria-label="Previous noticeboard posts">
                                {previousPosts.map((post) => {
                                    const postImageUrl = getRelatedPostImage(post);
                                    return (
                                        <RelatedCard key={post.uuid || post.title}>
                                            <RelatedMedia>
                                                {postImageUrl ? <img src={postImageUrl} alt={post.title} /> : <span>No media</span>}
                                            </RelatedMedia>
                                            <div>
                                                <RelatedTitle>{post.title}</RelatedTitle>
                                                <RelatedText>{post.message}</RelatedText>
                                            </div>
                                        </RelatedCard>
                                    );
                                })}
                            </RelatedScroller>
                        </ModalSection>
                    )}
                </>
            );
        }

        if (automationFeature === "survey") {
            const surveyEndDate = formatAutomationDate(automationDetail.finishDate || automationDetail.endDate);
            const surveyImageUrl = automationDetail.image?.imageUrl || automationDetail.imageUrl;
            const otherSurveys: RelatedSurvey[] = automationDetail.otherSurveys || [];

            return (
                <>
                    <ModalSection themeMode={themeMode}>
                        {surveyImageUrl && (
                            <PollMediaWrap>
                                <MediaPlaceholder themeMode={themeMode} ratio="16 / 9">
                                    <NoticeMedia src={surveyImageUrl} alt={automationDetail.surveyName || item.title} />
                                </MediaPlaceholder>
                            </PollMediaWrap>
                        )}
                        <ContentStack>
                            <PlainTitle>{automationDetail.surveyName || item.title}</PlainTitle>
                            <PostedDate>Ends {surveyEndDate}</PostedDate>
                            <StackList>
                                {(automationDetail.questions || []).map((question, index) => (
                                    <StackListItem key={`${index + 1}-${question}`}>
                                        <StackListNumber>{index + 1}</StackListNumber>
                                        <StackListText>{question}</StackListText>
                                    </StackListItem>
                                ))}
                            </StackList>
                            <ModalSectionNote>Head to the app to answer this survey and submit your response.</ModalSectionNote>
                        </ContentStack>
                    </ModalSection>
                    {otherSurveys.length > 0 && (
                        <ModalSection themeMode={themeMode}>
                            <ModalSectionHeader>
                                <ModalSectionTitle>Other available surveys</ModalSectionTitle>
                            </ModalSectionHeader>
                            <RelatedScroller aria-label="Other available surveys">
                                {otherSurveys.map((survey) => (
                                    <RelatedCard key={survey.uuid || survey.title}>
                                        <div>
                                            <RelatedTitle>{survey.title}</RelatedTitle>
                                            <RelatedText>Ends {formatAutomationDate(survey.endDate)}</RelatedText>
                                            <RelatedText>{compactCountLabel(survey.questionCount, "question", "questions")}</RelatedText>
                                        </div>
                                    </RelatedCard>
                                ))}
                            </RelatedScroller>
                        </ModalSection>
                    )}
                </>
            );
        }

        if (automationFeature === "poll") {
            const pollImageUrl = automationDetail.image?.imageUrl || automationDetail.imageUrl || automationDetail.pollImage;
            const pollEnd = formatAutomationDate(automationDetail.endsAt);
            const isPollEnded = hasPollEnded(automationDetail.endsAt, now);
            const otherPolls: RelatedPoll[] = automationDetail.otherPolls || [];

            return (
                <>
                    <ModalSection themeMode={themeMode}>
                        {pollImageUrl && (
                            <PollMediaWrap>
                                <MediaPlaceholder themeMode={themeMode} ratio="16 / 9">
                                    <NoticeMedia src={pollImageUrl} alt={automationDetail.pollName || item.title} />
                                </MediaPlaceholder>
                                <CountdownBadge>{getCountdownLabel(automationDetail.endsAt, now)}</CountdownBadge>
                            </PollMediaWrap>
                        )}
                        <ContentStack>
                            <PlainTitle>{automationDetail.question || item.description}</PlainTitle>
                            <PostedDate>{isPollEnded ? "Ended" : "Ends"} {pollEnd}</PostedDate>
                            <VoteTally>{compactCountLabel(automationDetail.voteCount, "vote", "votes")}{isPollEnded ? "" : " so far"}</VoteTally>
                            <div style={{ display: "grid", gap: 8 }}>
                                {(automationDetail.options || []).map((option, index) => (
                                    <AnswerOption key={option}>
                                        <StackListNumber>{index + 1}</StackListNumber>
                                        <span>{option}</span>
                                    </AnswerOption>
                                ))}
                            </div>
                            <ModalSectionNote>Head to the app to answer this poll and submit your response.</ModalSectionNote>
                        </ContentStack>
                    </ModalSection>
                    {otherPolls.length > 0 && (
                        <ModalSection themeMode={themeMode}>
                            <ModalSectionHeader>
                                <ModalSectionTitle>Other available polls</ModalSectionTitle>
                            </ModalSectionHeader>
                            <RelatedScroller aria-label="Other available polls">
                                {otherPolls.map((poll) => (
                                    <RelatedCard key={poll.uuid || poll.title}>
                                        <RelatedMedia>
                                            {poll.imageUrl ? <img src={poll.imageUrl} alt={poll.title} /> : <span>No image</span>}
                                        </RelatedMedia>
                                        <div>
                                            <RelatedTitle>{poll.title}</RelatedTitle>
                                            <RelatedText>Ends {formatAutomationDate(poll.endDate)}</RelatedText>
                                            <RelatedText>{compactCountLabel(poll.optionCount, "option", "options")}</RelatedText>
                                        </div>
                                    </RelatedCard>
                                ))}
                            </RelatedScroller>
                        </ModalSection>
                    )}
                </>
            );
        }

        return null;
    };

    return (
        <ModalCard themeMode={themeMode}>
            <ModalHeaderBlock themeMode={themeMode}>
                <ModalHeaderTop>
                    <ModalTitleBlock>
                        <ModalTitle>{modalTitle}</ModalTitle>
                    </ModalTitleBlock>
                    <ModalHeroPills>
                        <ModalHeroPill background={`${AUDIENCE_COLORS[item.audience] || "var(--calendar-accent)"}22`} color={AUDIENCE_COLORS[item.audience] || "var(--calendar-accent)"}>
                            {visibilityLabel}
                        </ModalHeroPill>
                        <ModalHeroPill background={`${displayTypeColor}22`} color={displayTypeColor}>
                            {displayTypeLabel}
                        </ModalHeroPill>
                    </ModalHeroPills>
                </ModalHeaderTop>
            </ModalHeaderBlock>
            <ModalScroll>
                {renderAutomationContent()}
            </ModalScroll>
            <ModalFooter themeMode={themeMode} style={{ justifyContent: "flex-end" }}>
                <div style={{ display: "flex", gap: 8 }}>
                    <ModalCancelButton themeMode={themeMode} type="button" onClick={onClose}>Close</ModalCancelButton>
                    {VIEWABLE_AUTOMATION_FEATURES.has(automationFeature) && (
                        <ModalPrimaryButton themeMode={themeMode} type="button" onClick={() => onViewFeature(item)}>
                            {`View ${displayTypeLabel}`}
                        </ModalPrimaryButton>
                    )}
                </div>
            </ModalFooter>
        </ModalCard>
    );
}

export default CalendarAutomationDetailModalContent;
